import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { getProduct, PRODUCTS } from "@/data/products";
import { MaskedLines, Reveal, Eyebrow, EASE } from "@/components/site/Motion";
import { EmbeddedSite } from "@/components/product/EmbeddedSite";
import { SystemEditions } from "@/components/product/SystemEditions";
import { CtaBand } from "@/components/home/CtaBand";
import NotFound from "./NotFound";

const ProductHero = ({ p }) => (
  <section className="relative -mt-20 pt-32 lg:pt-40 pb-16 grid-lines overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-end">
      <div className="lg:col-span-7">
        <Eyebrow>Produkt · {p.label}</Eyebrow>
        <MaskedLines testId="product-title" delay={0.1} className="font-display font-bold tracking-[-0.035em] leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mt-6" lines={p.title} />
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.55 }} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#3A3F5C]">{p.intro}</motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.7 }} className="mt-10 flex flex-wrap gap-3">
          {p.url && (
            <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`${p.linkTestId}-hero`} className="inline-flex items-center gap-2 rounded-full bg-[#0B14A0] text-white px-7 py-4 font-semibold transition-colors hover:bg-[#070B2E]">
              Gå till {p.host} <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {!p.noDemo && (
          <Link to={`/kontakt?subject=${encodeURIComponent("Boka demo")}`} data-testid="product-cta-demo" className="inline-flex items-center gap-2 rounded-full border border-[#070B2E]/20 bg-white/60 px-7 py-4 font-semibold transition-colors hover:bg-white">
            Boka demo <ArrowUpRight className="w-4 h-4" />
          </Link>
          )}
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.3, ease: EASE, delay: 0.25 }} className="lg:col-span-5">
        {p.screenshot ? (
          <div className="relative rounded-[22px] overflow-hidden border border-[#D8D7CE] bg-white shadow-[0_40px_80px_-40px_rgba(7,11,46,0.5)]" data-testid="product-screenshot">
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#E3E2DA] bg-[#F7F6F1]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#E3E2DA]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E3E2DA]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E3E2DA]" />
              <span className="ml-3 font-mono text-[11px] text-[#3A3F5C] truncate">{p.host}</span>
            </div>
            <img src={p.image} alt={`Skärmbild av ${p.short}`} className="block w-full aspect-[1440/1100] object-cover object-top" />
          </div>
        ) : (
        <div className="clip-notch relative aspect-[4/3] lg:aspect-[4/5] overflow-hidden rounded-[28px] bg-[#070B2E]">
          <img src={p.image} alt={p.short} className="absolute inset-0 w-full h-full object-cover opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070B2E]/80 to-transparent" />
          <p className="absolute bottom-6 left-6 font-display text-3xl font-bold text-white">{p.short}</p>
        </div>
        )}
      </motion.div>
    </div>
  </section>
);

const Highlights = ({ items }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {items.map((h, i) => (
      <Reveal key={h.t} delay={i * 0.06} className="h-full">
        <div className="h-full border-t-2 border-[#0B14A0] pt-5" data-testid={`product-highlight-${i}`}>
          <span className="font-mono text-xs text-[#0B14A0]">0{i + 1}</span>
          <h3 className="font-display text-xl font-semibold mt-2">{h.t}</h3>
          <p className="text-[#3A3F5C] mt-2 leading-relaxed">{h.d}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

const OtherProducts = ({ current }) => (
  <div className="flex flex-wrap gap-3">
    {PRODUCTS.filter((x) => x.slug !== current).map((x) => (
      <Link key={x.slug} to={`/produkter/${x.slug}`} data-testid={`other-product-${x.slug}`} className="group inline-flex items-center gap-2 rounded-full border border-[#D8D7CE] bg-white px-5 py-3 font-semibold transition-colors hover:border-[#0B14A0] hover:text-[#0B14A0]">
        {x.short} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
      </Link>
    ))}
  </div>
);

export default function ProductPage() {
  const { slug } = useParams();
  const p = getProduct(slug);
  if (!p) return <NotFound />;

  return (
    <div data-testid={`product-page-${p.slug}`}>
      <ProductHero p={p} />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16"><Highlights items={p.highlights} /></section>
      {p.systems && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <Reveal><h2 className="font-display text-4xl sm:text-5xl font-bold tracking-[-0.03em] mb-10">Välj ditt system</h2></Reveal>
          <SystemEditions systems={p.systems} />
        </section>
      )}
      {p.url && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="embedded-section">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 px-2 sm:px-0">
              <div>
                <Eyebrow>Live från {p.host}</Eyebrow>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-[-0.03em] mt-3">Utforska {p.short}</h2>
              </div>
              <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={p.linkTestId} className="inline-flex items-center gap-2 font-semibold text-[#0B14A0] hover:underline">
                {p.url} <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}><EmbeddedSite url={p.url} host={p.host} name={p.short} testId={`${p.linkTestId}-frame`} /></Reveal>
        </section>
      )}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#0B14A0] mb-5">Fler produkter</p>
        <OtherProducts current={p.slug} />
      </section>
      {p.noDemo ? <div className="pb-12" /> : <CtaBand />}
    </div>
  );
}
